<template>
    <nav
        class="Pagination"
        v-if="hasData"
        :data-current-page="currentPage"
        :data-total-pages="totalPages"
    >
        <div class="ButtonContainer">
            <PaginationButton
                v-if="shouldDisplayPrevious"
                title="Previous page"
                :rel="prevRel"
                :href="prevHref"
                name="previous"
                @click="store.setPage(currentPage - 1)"
            >
                <i class="lnr-chevron-left ChevronIcon"></i>
            </PaginationButton>
        </div>

        <span
            class="Text"
        >
            Page {{ formatCount(currentPage) }} of {{ formatCount(totalPages) }}
        </span>

        <div class="ButtonContainer">
            <PaginationButton
                v-if="currentPage < totalPages"
                title="Next page"
                :rel="nextRel"
                :href="nextHref"
                name="next"
                @click="store.setPage(currentPage + 1)"
            >
                <i class="lnr-chevron-right ChevronIcon"></i>
            </PaginationButton>
        </div>
    </nav>
</template>

<script lang="ts" setup>
import { computed } from 'vue';
import PaginationButton from './PaginationButton/PaginationButton.vue';
import { useSearchStore } from '@app/stores/useSearchStore';
import { useRouter } from 'vue-router';
import { formatCount } from '@app/utils/formatCount';

// TODO: Make it search type specific in the future

const store = useSearchStore();
const router = useRouter();
const resultsLength = computed(() => store.pageData?.totalResultsCount || 0);
const currentPage = computed(() => store.currentPage);
const hasData = computed(() => resultsLength.value && resultsLength.value > 0);
const shouldDisplayPrevious = computed(() => currentPage.value > 1 );
const totalPages = computed(() => store.pageData?.pagesCount || 1);

const getPageHref = (pageNumber: number): string => {
    const { page: _currentPage, ...queryWithoutPage } = router.currentRoute.value.query;

    if (pageNumber === 1) {
        return router.resolve({
            path: router.currentRoute.value.path,
            query: queryWithoutPage,
        }).href;
    }

    return router.resolve({
        path: router.currentRoute.value.path,
        query: {
            ...queryWithoutPage,
            page: pageNumber.toString(),
        },
    }).href;
};

const nextHref = computed<string>(() => getPageHref(currentPage.value + 1));
const prevHref = computed<string>(() => getPageHref(currentPage.value - 1));

const prevRel = computed<string>(() => shouldDisplayPrevious.value && currentPage.value !== 2 ? 'nofollow' : 'prev');
const nextRel = computed<string>(() => currentPage.value > 1 || currentPage.value === totalPages.value ? 'nofollow' : 'next');
</script>

<style scoped lang="scss">
.Pagination {
    display: flex;
    height: 3.25rem;
    justify-content: space-between;
    align-items: center;

    @include PaddingAreas(1, 0);

    .ButtonContainer {
        height: 3rem;
        width: 3rem;
    }

    .Text {
        @include TextStyle(UiLargeRegular);
    }

    .ChevronIcon {
        font-size: 1.5rem;
    }

    @include Breakpoint(Medium, Large, ExtraLarge) {
        justify-content: center;
        gap: 3rem;
    }
}
</style>
